import React, { useEffect, useState, useMemo } from "react";
import { Button, Divider, Form, Input, message, Select, Spin, Tag } from "antd";
import {
  EntityStatus,
  formItemLayout,
  ServiceTierActions,
  tailItemLayout,
} from "../../Constants/Constants";
import { onFormSubmit } from "../../ApiHandlers/ServiceTierApiHandlers";
import { apiCallback } from "../../ApiHandlers/Common";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { selectFilterFunction } from "../../Constants/Utilities";
import { convertDeviceModelListToDropdown } from "../../Helpers/DeviceModelHelpers";
import { convertServiceOfferingListToDropdown } from "../../Helpers/ServiceOfferingHelpers";
import { DiscardWarning } from "../Common/DiscardWarning";
import { BreadCrumbs } from "../Common/BreadCrumbs";

const getInitialValues = (record) => {
  return {
    status: EntityStatus.ACTIVE,
    serviceTierName: record?.serviceTierName,
  };
};

const getTitle = (action) => {
  switch (action) {
    case ServiceTierActions.add:
      return "Add";
    case ServiceTierActions.edit:
      return "Update";
    case ServiceTierActions.view:
      return "View";
    default:
      return "";
  }
};

export const ServiceTierForm = (props) => {
  const [processing, setProcessing] = useState(false);
  //data model info
  const [deviceModelData, setdeviceModelData] = useState([]);
  const [deviceModelDataLoading, setdeviceModelDataLoading] = useState(false);
  //service offering data
  const [serviceOfferingData, setserviceOfferingData] = useState([]);
  const [serviceOfferingDataLoading, setserviceOfferingDataLoading] =
    useState(false);
  const [form] = Form.useForm();
  const {
    record,
    action,
    onActionClick,
    serviceOfferingApi,
    history,
    deviceModelApi,
  } = props;
  const isAddForm = action === ServiceTierActions.add;
  const isViewForm = action === ServiceTierActions.view;

  const [selectedModel, setSelectedModel] = useState(null);
  const [existingOfferings, setExistingOfferings] = useState([]);
  const title = useMemo(() => getTitle(action), [action]);
  const [initialValues, setInitialValues] = useState(getInitialValues(record));

  useEffect(() => {
    if (
      action === ServiceTierActions.add ||
      action === ServiceTierActions.edit
    ) {
      fetchDeviceModelData();
      fetchServiceOfferingData();
    }
  }, []);

  const fetchDeviceModelData = () => {
    console.log("fetchDeviceModelData...");
    setdeviceModelDataLoading(true);
    const errorCallback = (error) => {
      setdeviceModelDataLoading(false);
      message.error("Error fetching Service Entitlement list");
    };
    try {
      deviceModelApi.getDeviceModels(
        apiCallback({
          history,
          errorCallback,
          successCallback: (data) => {
            const deviceModelData = convertDeviceModelListToDropdown(data);
            console.log("fetchDeviceModelData data", deviceModelData);
            setdeviceModelData(deviceModelData);
            setdeviceModelDataLoading(false);
          },
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const fetchServiceOfferingData = () => {
    setserviceOfferingDataLoading(true);
    const errorCallback = (error) => {
      setserviceOfferingDataLoading(false);
      message.error("Error fetching Service Entitlement details!");
    };

    try {
      serviceOfferingApi.getAllServiceOfferings(
        apiCallback({
          history,
          errorCallback,
          successCallback: (data) => {
            const serviceOfferingData =
              convertServiceOfferingListToDropdown(data);
            console.log("fetchServiceOfferingData data", serviceOfferingData);
            setserviceOfferingDataLoading(false);
            setserviceOfferingData(serviceOfferingData);
          },
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const onBackClick = () => {
    const isChanged =
      action === ServiceTierActions.view ? false : checkForInputChanges();
    DiscardWarning({ isChanged, onActionClick })();
  };

  const checkForInputChanges = () => {
    const fieldsToCheck = [
      "serviceTierName",
      "deviceModel",
      "serviceOfferings",
      "status",
    ];
    const values = form.getFieldsValue();
    let isInputsChanged = false;
    fieldsToCheck.every((f) => {
      if (
        values.hasOwnProperty(f) &&
        values[f] !== initialValues[f] &&
        (!!values[f] || !!initialValues[f])
      ) {
        isInputsChanged = true;
        return false;
      }
      return true;
    });
    return isInputsChanged;
  };

  useEffect(() => {
    const offeringData = record?.offeringData?.data || [];
    const modelOfferings = offeringData.filter(
      (item) => item.deviceModelId === selectedModel
    );
    const existingOfferingIds = modelOfferings.map((item) => item.offerId);
    setExistingOfferings(existingOfferingIds);
  }, [record, selectedModel]);

  return (
    <div className="list-container">
      <div className="page-header-container">
        <BreadCrumbs
          headers={props.parentBread.concat(`${title} Service Tier`)}
          onParentActionClick={onBackClick}
        />
      </div>
      <Divider className="divider" />
      <div className="form-data-container">
        <fieldset disabled={isViewForm}>
          <Form
            form={form}
            name="service-tier-form"
            layout="horizontal"
            onFinish={onFormSubmit({ props, setProcessing, setInitialValues })}
            initialValues={initialValues}
            requiredMark={"optional"}
            size={"middle"}
            {...formItemLayout}
          >
            <Spin spinning={processing} tip={"Processing, please wait..."}>
              <Form.Item
                name="serviceTierName"
                label="Service Tier Name"
                rules={[
                  { required: true, message: "Please fill in this field" },
                ]}
              >
                <Input
                  title="Name should be unique"
                  style={{ width: "90%", maxWidth: "250px" }}
                  disabled={!isAddForm}
                />
              </Form.Item>

              <Form.Item
                name="deviceModel"
                label="Device Model:"
                rules={[
                  {
                    required: true,
                    message: "Please select Device Model(s)",
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  style={{ width: "90%", maxWidth: "300px" }}
                  loading={deviceModelDataLoading}
                  disabled={deviceModelDataLoading || isViewForm}
                  onChange={(id) => setSelectedModel(id)}
                  filterOption={selectFilterFunction}
                  placeholder={
                    deviceModelDataLoading
                      ? "Loading Device Model list..."
                      : "Select any Device Model"
                  }
                >
                  {deviceModelData.map((item) => (
                    <Select.Option key={item.modelId} title={item.modelNumber}>
                      {item.modelNumber}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="serviceOfferings"
                label="Service Offerings"
                rules={[
                  {
                    required: true,
                    message: "Please select Service Offering(s)",
                  },
                ]}
              >
                <Select
                  allowClear
                  mode="multiple"
                  style={{ width: "90%", maxWidth: "350px" }}
                  loading={serviceOfferingDataLoading}
                  disabled={serviceOfferingDataLoading || isViewForm}
                  filterOption={selectFilterFunction}
                  placeholder={
                    serviceOfferingDataLoading
                      ? "Loading Service Offerings..."
                      : "Select Service Offerings"
                  }
                >
                  {serviceOfferingData.map((item) => (
                    <Select.Option
                      key={item.offerId}
                      disabled={existingOfferings.includes(item.offerId)}
                      title={
                        existingOfferings.includes(item.offerId)
                          ? "Offering already exist in Tier"
                          : item.offeringName
                      }
                    >
                      {item.offeringName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              {isAddForm && (
                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true }]}
                >
                  <Tag
                    color={
                      initialValues.status === EntityStatus.ACTIVE
                        ? "green"
                        : "red"
                    }
                  >
                    {initialValues.status}
                  </Tag>
                </Form.Item>
              )}

              {!isViewForm && (
                <Form.Item {...tailItemLayout}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="submit-button"
                  >
                    {title}
                  </Button>
                  <Button
                    htmlType="button"
                    onClick={() => form.resetFields()}
                    className="cancel-button"
                  >
                    Cancel
                  </Button>
                </Form.Item>
              )}
            </Spin>
          </Form>
        </fieldset>
      </div>
    </div>
  );
};
ServiceTierForm.propTypes = {
  action: PropTypes.string,
  record: PropTypes.object,
  deviceModelRecord: PropTypes.object,
  onSubmit: PropTypes.func,
  onActionClick: PropTypes.func,
  parentBread: PropTypes.array,

  deviceModelApi: PropTypes.object,
  serviceTierApi: PropTypes.object,
  serviceOfferingApi: PropTypes.object,
};

const mapStateToProps = (state) => ({
  deviceModelApi: state.apis.deviceModelApi,
  serviceTierApi: state.apis.serviceTierApi,
  serviceOfferingApi: state.apis.serviceOfferingApi,
});
const connectedServiceTierForm = connect(mapStateToProps)(ServiceTierForm);
export default withRouter(connectedServiceTierForm);
